'use client';

import { InputHTMLAttributes, useEffect, useRef, useState } from 'react';
import MaskedInput from 'react-text-mask';
import { ReactComponent as CloseIcon } from '@assets/icons/close.svg';
import { ReactComponent as Eye } from '@assets/icons/eye.svg';
import { ReactComponent as EyeSlash } from '@assets/icons/eye-slash.svg';
import { anyCharacterMask } from 'constant';
import { FieldInputProps, FormikErrors } from 'formik';

import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputProps,
  InputRightElement,
  StyleProps,
  SystemStyleObject,
  Text,
  useTheme
} from '@chakra-ui/react';

type TextFieldProps = InputProps &
  FieldInputProps<string> & {
    label?: string;
    error?:
      | string
      | string[]
      | FormikErrors<Record<string, unknown>>
      | FormikErrors<Record<string, unknown>>[];
    touched?: boolean;
    type?: string;
    size?: 'md' | 'lg' | 'xl';
    onReset: () => void;
    isFieldValue: boolean;
    containerStyles?: StyleProps | Record<string, StyleProps>;
    labelStyles?: StyleProps | Record<string, StyleProps>;
    helperText?: string;
    inputStyles?: InputProps;
    rightElementProps?: StyleProps | Record<string, SystemStyleObject>;
    mask?: false | (string | RegExp)[];
    guide?: boolean;
    showReset?: boolean;
    isFocused?: boolean;
  };

const helperTextStyles = {
  position: 'absolute',
  fontSize: '9px',
  fontFamily: 'main',
  fontWeight: 'medium',
  lineHeight: '12px',
  textTransform: 'uppercase',
  color: 'red.300',
  noOfLines: 1,
  marginTop: '6px'
};

const TextField = ({
  label,
  touched,
  error,
  onReset,
  isFieldValue,
  isRequired,
  containerStyles,
  labelStyles,
  inputStyles,
  rightElementProps,
  helperText,
  guide = false,
  mask,
  type = 'text',
  size = 'md',
  width = '100%',
  showReset = false,
  isFocused = false,
  ...inputProps
}: TextFieldProps) => {
  const { colors } = useTheme();
  const [secure, setSecure] = useState(true);
  const inputRef = useRef<MaskedInput | null>(null);

  const inputMask = mask || anyCharacterMask;
  const isSecureInput = type === 'password';
  const fieldType = isSecureInput && !secure ? 'text' : type;
  const isPasswordDataVisible = isSecureInput && fieldType === 'text';
  const inputType = fieldType !== 'password' ? 'text' : fieldType;

  const isErrorExist = error && typeof error === 'string' && touched;
  const inputIconPaddingRight = showReset && isSecureInput ? '65px' : '35px';
  const inputPaddingRight =
    !isSecureInput && !showReset ? '10px' : inputIconPaddingRight;

  const visibilityIcon = isPasswordDataVisible ? (
    <EyeSlash fill={colors.gray[250]} width={17} height={17} />
  ) : (
    <Eye fill={colors.gray[250]} width={17} height={17} />
  );

  const getInputMask = (value: string) => {
    const isNumberInput = type === 'number';
    const digits = value.match(/\d/g) || [];
    const onlyNumbers = digits.map(() => /\d/);

    return isNumberInput && !mask ? onlyNumbers : inputMask;
  };

  const toggleSecure = () => setSecure((prevState) => !prevState);

  useEffect(() => {
    if (isFocused && inputRef?.current) {
      inputRef.current.inputElement.focus();
    }
  }, [isFocused]);

  return (
    <FormControl
      position='relative'
      isInvalid={!!isErrorExist}
      width={width}
      isRequired={isRequired}
      sx={containerStyles}
    >
      {label && (
        <FormLabel
          fontSize='11px'
          color='gray.375'
          textTransform='uppercase'
          mb='5px'
          mr={0}
          className='label'
          minHeight='14px'
          requiredIndicator={
            <Text as='sup' color='red.300' marginLeft='2px'>
              *
            </Text>
          }
          sx={{ ...labelStyles }}
        >
          {label}
        </FormLabel>
      )}

      <InputGroup alignItems='center'>
        <Input
          ref={(el) => {
            if (el) {
              // @ts-ignore - MaskedInput returns a component with `inputElement`
              inputRef.current = el;
            }
          }}
          as={MaskedInput}
          type={inputType}
          mask={getInputMask}
          guide={guide}
          variant='brand'
          size={size}
          style={
            {
              paddingRight: inputPaddingRight,
              ...inputStyles
            } as InputHTMLAttributes<HTMLInputElement>
          }
          {...inputProps}
        />

        {isFieldValue && (
          <InputRightElement height='100%' {...rightElementProps}>
            {isSecureInput && (
              <Button
                position='absolute'
                left={showReset ? '-30px' : '0'}
                onClick={toggleSecure}
                bg='transparent'
                width='inherit'
                borderRadius={0}
                padding={0}
                _hover={{
                  opacity: 0.5
                }}
                _active={{
                  background: 'transparent'
                }}
              >
                {visibilityIcon}
              </Button>
            )}
            {showReset && (
              <Button
                onClick={onReset}
                bg='transparent'
                borderRadius={0}
                padding={0}
                minWidth='unset'
                className='close-icon'
                width='inherit'
                _hover={{
                  opacity: 0.5
                }}
                _active={{
                  background: 'transparent'
                }}
              >
                <CloseIcon fill={colors.gray[250]} width={10} height={16} />
              </Button>
            )}
          </InputRightElement>
        )}
      </InputGroup>

      {helperText && (
        <FormHelperText sx={helperTextStyles}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default TextField;
